import axios, { AxiosResponse } from 'axios'
import { FilterDTO } from 'medere-ui-framework'
import { axiosInstance } from '../App'
import { LoadConfigDTO, SortingFieldDTO } from '../dto/LoadConfigDTO'
import {
  HospitalizationBedDTO,
  HospitalizationCriteriaDTO,
  HospitalizationEvolutionRowDTO,
  HospitalizationExportFormat,
  HospitalizationExportScope,
  HospitalizationFilterDTO,
  HospitalizationGroupBy,
  HospitalizationGroupsDTO,
  HospitalizationOptionsDTO,
  HospitalizationRowDTO,
  HospitalizationSummaryDTO,
} from '../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import { ApiErrorResponse, parseApiError } from './apiError'

/** Prefijo de los endpoints securizados: se define una sola vez para esta capa de servicios. */
export const SECURED_API_BASE = '/api/secured'
const HOSPITALIZATION_SUMMARY_API = `${SECURED_API_BASE}/hospitalization-summary`

/** Códigos de error del backend que la pantalla distingue. */
export const HOSPITALIZATION_ACCESS_DENIED = 'ACCESS_DENIED'

/**
 * Lo que `RemoteTable` le pasa a `getItems` y a la exportación: sus filtros de columna y su orden, más los filtros de la
 * pantalla que la pantalla le da en `params` (no son de columna: viajan aparte, en `filters`).
 */
export interface HospitalizationTableParams {
  filters?: HospitalizationFilterDTO
  filterDTO?: FilterDTO | null
  multiSortMeta?: { fieldName: string; ascOrder: boolean }[]
}

/** Las grillas de la pantalla; cada una tiene su consulta y su exportación. */
export type HospitalizationGrid = 'list' | 'census' | 'evolutions'

const GRID_SCOPE: Record<HospitalizationGrid, HospitalizationExportScope> = { list: 'LIST', census: 'CENSUS', evolutions: 'EVOLUTIONS' }

/** Error del backend ya traducido al contrato `ErrorResponse`: el componente nunca ve el error crudo de axios. */
export class HospitalizationApiError extends Error {
  readonly status: number
  readonly errorCode: string

  constructor(apiError: ApiErrorResponse) {
    super(apiError.message)
    this.name = 'HospitalizationApiError'
    this.status = apiError.status
    this.errorCode = apiError.errorCode
  }
}

/** Con `responseType: 'blob'` el cuerpo del error también llega como Blob: se lee para que `parseApiError` vea el JSON. */
async function unwrapBlobError(error: unknown): Promise<void> {
  if (!axios.isAxiosError(error) || !error.response || !(error.response.data instanceof Blob)) return
  try {
    error.response.data = JSON.parse(await error.response.data.text()) as unknown
  } catch {
    error.response.data = null
  }
}

/** Único punto donde se parsea el error de axios (contrato `ErrorResponse`). */
async function toHospitalizationApiError(error: unknown): Promise<HospitalizationApiError> {
  await unwrapBlobError(error)
  return new HospitalizationApiError(parseApiError(error))
}

/** True si el pedido se canceló con el `AbortController`: no es un error para mostrar. */
export function isRequestCanceled(error: unknown): boolean {
  return axios.isCancel(error)
}

/** True si el backend respondió que el perfil no tiene acceso (403 `ACCESS_DENIED`). */
export function isAccessDenied(error: unknown): boolean {
  return error instanceof HospitalizationApiError && error.errorCode === HOSPITALIZATION_ACCESS_DENIED
}

/**
 * Mensaje para mostrar: el del backend si es un error de negocio (4xx: rango inválido, sin permiso), que ya viene
 * filtrado y dice qué hacer; si no (red, servidor), el texto genérico de la acción que falló.
 */
export function errorMessageOf(error: unknown, fallback: string): string {
  return error instanceof HospitalizationApiError && error.status >= 400 && error.status < 500 ? error.message : fallback
}

async function request<TResponse>(
  path: string,
  body: unknown,
  signal?: AbortSignal,
  responseType?: 'blob',
): Promise<AxiosResponse<TResponse>> {
  try {
    return await axiosInstance.post<TResponse>(`${HOSPITALIZATION_SUMMARY_API}${path}`, body, { signal, responseType })
  } catch (error: unknown) {
    if (axios.isCancel(error)) throw error
    throw await toHospitalizationApiError(error)
  }
}

async function post<TResponse>(path: string, body: unknown, signal?: AbortSignal): Promise<TResponse> {
  return (await request<TResponse>(path, body, signal)).data
}

/** Paginación, orden y filtros de columna de `RemoteTable` en el formato del backend (`LoadConfigDTO`). Sin página para el export. */
function buildLoadConfig(offSet: number | null, pageSize: number | null, params?: HospitalizationTableParams): LoadConfigDTO {
  const sorting: SortingFieldDTO[] = (params?.multiSortMeta ?? []).map((sort) => ({
    fieldName: sort.fieldName,
    ascOrder: sort.ascOrder,
    sqlFormula: null,
    compositeSortOrder: null,
  }))
  const filterDTO = params?.filterDTO
  const filters = filterDTO && filterDTO.filters.length > 0
    ? {
      associationType: filterDTO.associationType,
      filters: filterDTO.filters.map((filter) => ({
        fieldName: filter.fieldName,
        filterType: filter.filterType,
        sqlType: filter.sqlType ?? null,
        values: filter.values,
        sql: null,
      })),
      filterLists: [],
    }
    : null
  return { offSet, pageSize, sortingFieldInfoList: sorting, filters }
}

/** El cuerpo de las grillas: sin filtros de pantalla el backend rechaza el pedido, así que acá son obligatorios. */
function criteria(
  offSet: number | null,
  pageSize: number | null,
  params: HospitalizationTableParams,
): HospitalizationCriteriaDTO {
  if (!params.filters) throw new Error('Faltan los filtros de la pantalla')
  return { filters: params.filters, loadConfigDTO: buildLoadConfig(offSet, pageSize, params) }
}

/** Indicadores del período y del anterior, serie diaria y repartos: una vez por cambio de filtros de pantalla (o de día, en el panel). */
export function getHospitalizationSummary(filters: HospitalizationFilterDTO, signal?: AbortSignal): Promise<HospitalizationSummaryDTO> {
  return post<HospitalizationSummaryDTO>('/summary', { filters }, signal)
}

/** Las internaciones del período agrupadas por sector, financiador, tipo de ingreso o motivo de egreso (tabla completa en memoria). */
export function getHospitalizationGroups(
  filters: HospitalizationFilterDTO,
  groupBy: HospitalizationGroupBy,
  signal?: AbortSignal,
): Promise<HospitalizationGroupsDTO> {
  return post<HospitalizationGroupsDTO>('/groups', { filters, groupBy }, signal)
}

/** Las camas de un sector con su ocupación en el período: se piden al expandir el sector. */
export function getHospitalizationBeds(
  filters: HospitalizationFilterDTO,
  sectorId: number,
  signal?: AbortSignal,
): Promise<HospitalizationBedDTO[]> {
  return post<HospitalizationBedDTO[]>('/beds', { filters, sectorId }, signal)
}

/** Opciones de las listas de los filtros y permiso de evoluciones. */
export function getHospitalizationOptions(signal?: AbortSignal): Promise<HospitalizationOptionsDTO> {
  return post<HospitalizationOptionsDTO>('/options', undefined, signal)
}

/** Una página de una grilla (scroll infinito), con los filtros de pantalla y los de columna. El total no se muestra: se descarta. */
async function getRows<TRow>(path: string, offSet: number, pageSize: number, params: HospitalizationTableParams): Promise<TRow[]> {
  return (await post<{ rows: TRow[] }>(path, criteria(offSet, pageSize, params))).rows
}

/** Una página de la lista: las internaciones que se superponen con el período. */
export function getHospitalizationRows(offSet: number, pageSize: number, params: HospitalizationTableParams): Promise<HospitalizationRowDTO[]> {
  return getRows<HospitalizationRowDTO>('/list', offSet, pageSize, params)
}

/** Una página de «Internados hoy»: los internados al momento, que no dependen del período. */
export function getHospitalizationCensusRows(offSet: number, pageSize: number, params: HospitalizationTableParams): Promise<HospitalizationRowDTO[]> {
  return getRows<HospitalizationRowDTO>('/census', offSet, pageSize, params)
}

/** Una página de evoluciones médicas del período (solo con el permiso de evoluciones). */
export function getHospitalizationEvolutionRows(
  offSet: number,
  pageSize: number,
  params: HospitalizationTableParams,
): Promise<HospitalizationEvolutionRowDTO[]> {
  return getRows<HospitalizationEvolutionRowDTO>('/evolutions', offSet, pageSize, params)
}

/**
 * Exporta el resumen (`SUMMARY`) o, con `groupBy`, el agrupado (`GROUPS`) en PDF o Excel con los filtros de la pantalla.
 * Devuelve la respuesta completa: `ExportMenu` baja el archivo con el nombre del `Content-Disposition`. El error se deja sin
 * traducir: `ExportMenu` lo lee (el cuerpo del error también llega como Blob) y muestra el mensaje del backend si es de negocio.
 */
export function exportHospitalizationSummary(
  filters: HospitalizationFilterDTO,
  groupBy: HospitalizationGroupBy | null,
  format: HospitalizationExportFormat,
): Promise<AxiosResponse<Blob>> {
  const body: HospitalizationCriteriaDTO = groupBy
    ? { filters, scope: 'GROUPS', groupBy, format }
    : { filters, scope: 'SUMMARY', format }
  return axiosInstance.post<Blob>(`${HOSPITALIZATION_SUMMARY_API}/export`, body, { responseType: 'blob' })
}

/**
 * Exporta una grilla completa con lo que se ve: los filtros de pantalla, los de columna, el buscador y el orden (`params` es
 * lo último que pidió la tabla; no se pagina). Sin `filtersText`, el backend escribe los filtros aplicados en el encabezado.
 */
export function exportHospitalizationGrid(
  grid: HospitalizationGrid,
  format: HospitalizationExportFormat,
  params: HospitalizationTableParams,
): Promise<AxiosResponse<Blob>> {
  const body: HospitalizationCriteriaDTO = { ...criteria(null, null, params), scope: GRID_SCOPE[grid], format }
  return axiosInstance.post<Blob>(`${HOSPITALIZATION_SUMMARY_API}/export`, body, { responseType: 'blob' })
}
